<script setup lang="ts">
/**
 * **错误边界** —— 包贡献的组件崩了，别把整个窗口带走。
 *
 * ## 为什么需要它
 *
 * 编辑器主体和侧栏面板都可以由**包**提供（`EditorContribution.component` /
 * `PanelTabDef.component`），而那些代码不是内核写的。它渲染时抛一个异常，
 * Vue 的默认行为是：整个组件子树渲染失败 —— 用户看到的是**一块空白**，
 * 而错误只在浏览器控制台里（生产环境用户根本看不到）。
 *
 * 内核自己的编辑器没有这个问题（它们的代码在同一份测试里），
 * 所以这个边界**只包在包贡献的内容外面**，不改变内核那几条的行为。
 *
 * ## 三条行为
 *
 * 1. **吞掉**（`onErrorCaptured` 返回 `false`）：不再往上冒到
 *    `app.config.errorHandler` —— 一个插件的渲染错误不该被当成
 *    "整个应用崩了"；
 * 2. **说清楚是谁**：卡片上有窗口名 / 包 id / 原始错误消息 ——
 *    用户至少要能把它报给写包的人，而不是"界面白了"；
 * 3. **能重试**：`重试` 按钮靠换 `key` 把子树重建一次
 *    （很多渲染错误是"某一次数据不对"，重建就能过去）。
 *
 * ⚠️ 这是**容错**，不是**隔离**：边界挡不住包去改内核的状态。
 * 真正的隔离是"包只拿到 `{ area }`"那条契约（见 `docs/node-packages.md` §6.4）。
 */
import { onErrorCaptured, ref, watch } from 'vue'

const props = defineProps<{
  /** 出错时显示给用户的标题（一般是窗口名） */
  label: string
  /** 这段内容来自哪个包（没有就不显示来源那一行） */
  source?: string
}>()

const message = ref<string | null>(null)
/** 换它 = 把子树整个重建一次（`重试`） */
const attempt = ref(0)

onErrorCaptured((error, _instance, info) => {
  message.value = `${error instanceof Error ? error.message : String(error)}（${info}）`
  /* ⚠️ 返回 false = **已处理**，不再往上冒（见文件头第 1 条） */
  return false
})

/*
 * 换了内容（用户切了窗口类型 / 换了面板页）就清掉错误状态 ——
 * 否则「一个包的面板崩过」会永久替换掉之后所有正常内容。
 */
watch(
  () => `${props.label}|${props.source ?? ''}`,
  () => {
    message.value = null
  },
)
</script>

<template>
  <div v-if="message" class="np-boundary" role="alert">
    <strong class="np-boundary__title">{{ label }}出错了</strong>
    <code class="np-boundary__message">{{ message }}</code>
    <span class="np-boundary__hint">
      {{ source ? `这一块来自节点包「${source}」` : '这一块来自节点包' }} ——
      编辑器其余部分不受影响，可以重试，或者在这个窗口的头部换回「节点图」。
    </span>
    <button class="np-boundary__retry" type="button" @click="attempt += 1; message = null">
      重试
    </button>
  </div>
  <!--
    ⚠️ 包一层 `display: contents` 的壳是为了**换 key 重建子树**，
    同时不给布局加一层盒子（`.np-area__main` 是 flex 容器，
    多一层 div 会让画布/面板的尺寸算错）。
  -->
  <div v-else :key="attempt" class="np-boundary__body">
    <slot />
  </div>
</template>

<style scoped>
.np-boundary__body {
  display: contents;
}

.np-boundary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: auto;
  max-width: 420px;
  padding: 14px;
  border: 1px solid var(--np-border-soft, rgba(255, 255, 255, 0.1));
  border-left: 2px solid var(--np-danger, #e05252);
  border-radius: var(--np-radius-l, 9px);
  background: var(--np-surface-2, rgba(32, 32, 32, 0.96));
  color: var(--np-panel-text, #e2e2e2);
  font-size: 11.5px;
}

.np-boundary__title {
  color: var(--np-danger, #e05252);
  font-size: 12px;
}

.np-boundary__message {
  overflow-wrap: anywhere;
  color: var(--np-panel-dim, #9a9a9a);
  /* ⚠️ 等宽字体在这份代码里一直是直接写栈（没有令牌），跟着它走 */
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.np-boundary__hint {
  color: var(--np-panel-dim, #9a9a9a);
  line-height: 1.5;
}

.np-boundary__retry {
  align-self: flex-start;
  height: var(--np-ctl-h-md, 26px);
  padding: 0 12px;
  border: 1px solid var(--np-border-soft, rgba(255, 255, 255, 0.1));
  border-radius: var(--np-radius-s, 4px);
  background: var(--np-surface-raised, rgba(255, 255, 255, 0.07));
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.np-boundary__retry:hover {
  background: var(--np-surface-3, rgba(255, 255, 255, 0.09));
}
</style>
